import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Container, Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";

import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import Dropdown from "../../components/Dropdown";

import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";

import addIcon from "../../assets/add_icon.png";
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import editIcon from "../../assets/edit_icon.png";
import closeIcon from "../../assets/close_icon.png";
import saveIcon from "../../assets/save_icon.png";
import classes from "../../components/Modal.module.css";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorBox from "../../components/ErrorBox";
import AreYouSureModal from "../../components/AreYouSureModal";
import useDebounce from "../../utils/useDebounce";
import PageWrapper from "../../components/PageWrapper";

export default function DoctorsPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [updateDoctorModal, setUpdateDoctorModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1); // Added pagination state
  const [totalPages, setTotalPages] = useState(1); // Added totalPages state
  const [totalRecords, setTotalRecords] = useState(0); // Added totalRecords state
  const [loadingModal, setLoadingModal] = useState(false);
  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const { userData } = useContext(UserAuth);

  const { error, setError, loading, setLoading } = useContext(DataContext);

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;
  const emptyInputField = error === "emptyInputFields" ? "emptyInputField" : "";

  // doctors states
  const options = [
    { name: "Doctor" },
    { name: "Healer" },
    { name: "Receptionist" },
  ];
  const [addDoctorData, setAddDoctorData] = useState({
    name: "",
    specialty: "",
    phone: "",
    role: "",
  });
  const [allDoctors, setAllDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [updateDoctorFields, setUpdateDoctorFields] = useState(null);
  const [doctorModal, setDoctorModal] = useState(false);
  const [doctorAdded, setDoctorAdded] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [errorFields, setErrorFields] = useState({});

  const validateFields = () => {
    let errors = {};
    let hasError = false;

    if (!addDoctorData.name.trim()) {
      errors["name"] = t("emptyInputField");
      hasError = true;
    }

    if (!addDoctorData.specialty.trim()) {
      errors["specialty"] = t("emptyInputField");
      hasError = true;
    }

    if (!addDoctorData.phone.trim()) {
      errors["phone"] = t("emptyInputField");
      hasError = true;
    }

    setErrorFields(errors);
    setErrorMessage(hasError ? t("emptyFields") : "");
    return !hasError;
  };

  const openUpdateDoctorModal = (doctor) => {
    setSelectedDoctor(doctor);
    setUpdateDoctorFields({
      name: doctor.name,
      phone: doctor.phone,
      specialty: doctor.specialty,
      role: "Doctor",
    });
    setIsEditing(false);
    setUpdateDoctorModal(true);
  };

  // handler functions
  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateDoctorFields({
      ...updateDoctorFields,
      [name]: value,
    });
  };

  const handleCancel = () => {
    setDoctorModal(false);

    setUpdateDoctorModal(false);
    setSelectedDoctor(null);

    setErrorFields("");

    setAddDoctorData({
      name: "",
      phone: "",
      specialty: "",
      role: "",
    });
  };

  const handleDoctorInput = (e) => {
    setAddDoctorData({
      ...addDoctorData,
      [e.target.name]: e.target.value,
    });

    setError("");
  };

  const handleUpdateDoctor = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);

    const payload = updateDoctorFields;

    try {
      setLoadingModal(true);
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/updateEmployee/${selectedDoctor.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Organization-Id": userData?.organizationId,
          },
        }
      );

      console.log(response);

      if (response.status === 200) {
        await getAllDoctors();
        setUpdateDoctorModal(false);
      }
      setLoadingModal(false);
    } catch (error) {
      setLoadingModal(false);
      console.error("Error updating doctor:", error);
    }
  };

  const getAllDoctors = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId = userData?.organizationId;

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllEmployees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": parseInt(userId),
            "organization-Id": parseInt(organizationId),
          },
          params: {
            page: currentPage,
            limit: 5,
            search: searchTerm,
            // role: "Doctor",
          },
        }
      );

      if (response?.status === 200) {
        setAllDoctors(response?.data?.employees);
        setLoading(false);
        setTotalPages(response?.data?.totalPages);
        setTotalRecords(response?.data?.count);
      }
    } catch (error) {
      console.log("all doctors", error);

      setLoading(false);
    }
  };

  useEffect(() => {
    getAllDoctors();
  }, [doctorAdded, debouncedSearch, currentPage]);

  // functin to add a new doctor to the database
  const addDoctor = async () => {
    if (!validateFields()) return;

    // const token = localStorage.getItem("token");
    // const userId = parseInt(localStorage.getItem("userId"), 10);

    const { token, userId, organizationId } = userData;

    const payload = {
      ...addDoctorData,
      organizationId: parseInt(organizationId),
    };

    try {
      setLoadingModal(true);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createEmployee`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Organization-Id": organizationId,
          },
        }
      );

      if (response.status == 200) {
        setLoadingModal(false);
        handleCancel();
        setDoctorAdded((prev) => !prev);
      }
    } catch (error) {
      console.log(error);
      setLoadingModal(false);
    }
  };

  const deleteEmployee = async () => {
    try {
      const token = userData?.token;
      if (!token) {
        console.error("Token is missing, aborting request.");
        return;
      }

      const id = selectedDoctor.id;

      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/deleteEmployee`,
        {
          data: { id },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 204) {
        console.log("DELETION FINISHED");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddDoctor = (e) => {
    e.preventDefault();
    addDoctor();
  };

  const handleDeleteAppointment = async () => {
    await deleteEmployee();
    await getAllDoctors();
    setShowAreYouSure(false);
    setUpdateDoctorModal(false);
  };

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}
          title={t("organizations")}
          withBackButton
      >

        <div className="users-title-section">
          <h4 className="page--title">{t("doctors", { defaultValue: "doctors" })}</h4>
          {userData?.userId ? (
            <CustomButton
              title={t("addDoctor")}
              variant="blank"
              icon={addIcon}
              iconSize={20}
              onClick={() => setDoctorModal(true)}
            />
          ) : null}
        </div>
        <CustomInput
          placeholder={t("search")}
          name="doctorName"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          resetSearch={() => {
            setSearchTerm("");
          }}
        />
        {loading ? (
          <Loader />
        ) : allDoctors.length === 0 ? (
          <p>{t("noDoctorsFound")}.</p>
        ) : (
          <ul className="users-list" style={{ direction: currentDirection }}>
            {allDoctors.map((doctor) => (
              <li
                className="user-list-item"
                key={doctor.id}
                onClick={() => openUpdateDoctorModal(doctor)}
                style={{ cursor: "pointer" }}
              >
                <p className="user-username">{doctor.name}</p>
                {doctor.specialty && (
                  <Typography variant="subtitle1" color="textSecondary">
                    {doctor.specialty}
                  </Typography>
                )}
              </li>
            ))}
          </ul>
        )}
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() => {
                setCurrentPage(currentPage - 1);
              }}
              disabled={currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
            />
            <span className="pagination-location">
              {t("page")}: {currentPage} - {totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() => {
                if (currentPage === totalPages) return;
                setCurrentPage(currentPage + 1);
              }}
              disabled={currentPage >= totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
            />
          </div>

          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalNumber")}: {totalRecords}
            </span>
          </div>
        </section>
        {updateDoctorModal && (
          <Modal
            title={isEditing ? t("editDoctor") : t("doctor")}
            onClose={handleCancel}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        onClick={handleUpdateDoctor}
                        title={t("confirm")}
                        icon={saveIcon}
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon={editIcon}
                        iconSize={18}
                      />

                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        // icon={}
                        iconSize={20}
                        onClick={() => setShowAreYouSure(true)}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div className={classes.flexableForm}>
              {/* {isEditing && error ? (
                <ErrorBox>
                  {t(error)}
                  {duplicateAccountNumber ? (
                    <span className="bold-text">{duplicateAccountNumber}</span>
                  ) : null}
                </ErrorBox>
              ) : null} */}
              {isEditing ? (
                <div className={classes.inputContainer}>
                  <span>{t("doctorName")}: </span>
                  <CustomInput
                    name="name"
                    value={updateDoctorFields?.name || ""}
                    onChange={(e) => handleUpdateInputChange(e)}
                    flex={true}
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("doctorName")}:</p>
                  <span>{selectedDoctor?.name}</span>
                </div>
              )}

              {isEditing ? (
                <section>
                  <div className={classes.inputContainer}>
                    <span>{t("medicalSpecialty")}: </span>
                    <CustomInput
                      name="specialty"
                      value={updateDoctorFields?.specialty || ""}
                      onChange={(e) => handleUpdateInputChange(e)}
                      flex={true}
                    />
                  </div>
                </section>
              ) : (
                <div className="view-user-entity">
                  <p>{t("medicalSpecialty")}:</p>
                  <span>{selectedDoctor?.specialty}</span>
                </div>
              )}
              {isEditing ? (
                <div className={classes.inputContainer}>
                  <span>{t("phoneNumber")}: </span>
                  <CustomInput
                    name="phone"
                    type="money"
                    value={updateDoctorFields?.phone || ""}
                    onChange={(e) => handleUpdateInputChange(e)}
                    flex={true}
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("phoneNumber")}:</p>
                  <span>{selectedDoctor?.phone}</span>
                </div>
              )}
            </div>
          </Modal>
        )}
        {doctorModal && (
          <Modal
            title="addDoctor"
            onClose={() => handleCancel()}
            withDropdown
            error={t(error)}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className={classes.actions}>
                  <CustomButton
                    onClick={handleAddDoctor}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => handleCancel()}
                    variant="blank"
                  />
                </section>
              </>
            }
          >
            <div className={classes.flexableForm}>
              {error ? <ErrorBox>{t(errorMessage)}</ErrorBox> : null}
              <section>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("doctorName")}: </span>
                  <CustomInput
                    name="name"
                    value={addDoctorData?.name || ""}
                    onChange={handleDoctorInput}
                    flex={true}
                    errorText={errorFields["name"]}
                  />
                </div>
              </section>

              <section>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("medicalSpecialty")}: </span>
                  <CustomInput
                    name="specialty"
                    value={addDoctorData?.specialty || ""}
                    onChange={handleDoctorInput}
                    flex={true}
                    errorText={errorFields["specialty"]}
                  />
                </div>
              </section>
              <section>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("phoneNumber")}: </span>
                  <CustomInput
                    name="phone"
                    value={addDoctorData.phone || ""}
                    onChange={handleDoctorInput}
                    type="money"
                    flex={true}
                    errorText={errorFields["phone"]}
                  />
                </div>
              </section>

              <section>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("role")}: </span>
                  <Dropdown
                    options={options}
                    setSelected={(item) => {
                      setAddDoctorData((prev) => {
                        return { ...prev, role: item.name };
                      });
                    }}
                    flex
                    thick
                    selected={addDoctorData?.role || ""}
                  />
                  {/* <CustomInput
                    name="phone"
                    value={addDoctorData.phone || ""}
                    onChange={handleDoctorInput}
                    type="money"
                    flex={true}
                    errorText={errorFields["phone"]}
                  /> */}
                </div>
              </section>
            </div>
          </Modal>
        )}
      </PageWrapper>
      {showAreYouSure ? (
        <AreYouSureModal
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleDeleteAppointment}
          customMessage="areYouSureYouWantToDelete"
          closeTitle="noDontDelete"
          confirmTitle="yesDelete"
        />
      ) : null}
    </div>
  );
}
